import { Link } from "@/components/link/link.component";
import { Text } from "@/components/text/text.component";

import { appendQueryString } from "@/utils/url.util";

export const OfferRedemption = ({ block }: OfferRedemptionProps) => {
  const steps = (block.steps ?? []).filter(
    (step): step is OfferRedemptionStep => Boolean(step?.title),
  );

  if (!block.title || steps.length === 0) return null;

  const ctaHref =
    block.ctaLink?.url != null
      ? appendQueryString(block.ctaLink.url, block.ctaLink.queryString)
      : null;

  return (
    <section
      data-testid="offer-redemption"
      className="mx-auto flex w-full max-w-360 flex-col items-center gap-10 px-6 py-14 lg:px-12">
      <div className="flex w-full flex-col items-center gap-4 text-center">
        <Text
          variant="h3"
          component="h2"
          className="text-greyscale-headline"
          data-testid="offer-redemption-title">
          {block.title}
        </Text>
        {block.subtitle ? (
          <Text
            variant="body1"
            className="text-greyscale-body"
            data-testid="offer-redemption-subtitle">
            {block.subtitle}
          </Text>
        ) : null}
      </div>

      <ul className="grid w-full grid-cols-1 gap-6 lg:grid-cols-3">
        {steps.map((step) => (
          <li
            key={step._key ?? step.title}
            className="bg-primary/15 flex flex-col items-center gap-4 rounded-2xl px-6 py-8 text-center">
            {step.label ? (
              <Text variant="body2" className="text-greyscale-body">
                {step.label}
              </Text>
            ) : null}
            <Text
              variant="h5"
              component="h3"
              className="text-greyscale-headline">
              {step.title}
            </Text>
            {step.description ? (
              <Text variant="body4" className="text-greyscale-body">
                {step.description}
              </Text>
            ) : null}
          </li>
        ))}
      </ul>

      {block.ctaLabel && ctaHref ? (
        <Link
          data-testid="offer-redemption-cta"
          secondary
          href={ctaHref}
          className="h-12 w-fit px-6"
          target={block.ctaLink?.openInNewTab ? "_blank" : undefined}
          rel={block.ctaLink?.openInNewTab ? "noopener noreferrer" : undefined}>
          {block.ctaLabel}
        </Link>
      ) : null}
    </section>
  );
};

export type OfferRedemptionStep = {
  _key?: string;
  label?: string | null;
  title?: string | null;
  description?: string | null;
};

export type OfferRedemptionBlock = {
  _type: "offerRedemption";
  _key?: string;
  title?: string | null;
  subtitle?: string | null;
  steps?: Array<OfferRedemptionStep | null> | null;
  ctaLabel?: string | null;
  ctaLink?: {
    url?: string | null;
    queryString?: string | null;
    openInNewTab?: boolean | null;
  } | null;
};

export type OfferRedemptionProps = {
  block: OfferRedemptionBlock;
};
